import { useEffect, useRef, useState, type ReactNode } from 'react';
import { addDays, addMonths, differenceInCalendarDays, differenceInCalendarMonths, endOfMonth, isSameDay, startOfMonth } from 'date-fns';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Button } from '@shared/ui/primitives/Button';
import { cn } from '@shared/lib/cn';
import { haptics } from '@shared/lib/haptics';
import { BOOKING_WINDOW_MONTHS, dayOf, startOfToday } from './visitorAccessFormat';
import { LABEL } from './visitorAccessStyles';

/** How long the PIN works, when the member picks it: days count both ends (a visit), nights the days between (a stay). */
export interface VisitLength {
  unit: 'day' | 'night';
  value: number;
  max: number;
  onChange: (value: number) => void;
}

interface VisitDatesProps {
  /** Above the visit day ("Visit day", "Arrives"). */
  dayLabel: string;
  start: Date;
  onStart: (day: Date) => void;
  length?: VisitLength;
  /** Without a length to pick, how many days the PIN works from the visit day; left out, no summary is shown. */
  days?: number;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const count = (n: number, unit: 'day' | 'night') => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** The last day the PIN works: a visit's last day, or the day a stay leaves. */
function lastDayOf(start: Date, length: VisitLength | undefined, days: number): Date {
  if (!length) return addDays(start, days - 1);
  return addDays(start, length.unit === 'day' ? length.value - 1 : length.value);
}

/** "Tue 27 Oct → Sat 7 Nov", or "Tue 27 Oct until 23:59" for one day. */
function spanOf(start: Date, last: Date): string {
  return isSameDay(start, last) ? `${dayOf(start)} until 23:59` : `${dayOf(start)} → ${dayOf(last)}`;
}

/**
 * The visit's dates as steppers: the visit day a day at a time, and how long the PIN works, which
 * also slides for big jumps. Tapping either value opens the calendar, and a line under them sums up
 * the days the PIN works.
 */
export function VisitDates({ dayLabel, start, onStart, length, days }: VisitDatesProps): JSX.Element {
  const [sheet, setSheet] = useState<'day' | 'until' | null>(null);

  // 1. The visit day stays between today and the end of the booking window.
  const today = startOfToday();
  const latest = addMonths(today, BOOKING_WINDOW_MONTHS);
  const last = lastDayOf(start, length, days ?? 1);
  const lengthCount = length ? count(length.value, length.unit) : count(differenceInCalendarDays(last, start) + 1, 'day');
  const dayName = isSameDay(start, today) ? 'Today' : isSameDay(start, addDays(today, 1)) ? 'Tomorrow' : DAY_NAMES[start.getDay()]!;

  return (
    <div className="flex flex-col gap-4.5">
      {/* 2. Visit day: back or on a day, or the calendar for any other. */}
      <div className="flex flex-col gap-2.5">
        <p className={LABEL}>{dayLabel}</p>
        <div className="flex items-center gap-2.5 rounded-[14px] border border-hairline bg-card px-3.5 py-3">
          <StepButton label="Day before" disabled={start <= today} onStep={() => onStart(addDays(start, -1))}>
            <ChevronLeft className="h-4.5 w-4.5" strokeWidth={2.2} />
          </StepButton>
          <ValueButton value={dayName} line={dayOf(start)} onClick={() => setSheet('day')} />
          <StepButton label="Day after" disabled={start >= latest} onStep={() => onStart(addDays(start, 1))}>
            <ChevronRight className="h-4.5 w-4.5" strokeWidth={2.2} />
          </StepButton>
        </div>
      </div>

      {/* 3. How long: one less or more, the calendar for the last day, or the slider for big jumps. */}
      {length ? (
        <div className="flex flex-col gap-2.5">
          <p className={LABEL}>How long</p>
          <div className="flex flex-col gap-2.5 rounded-[14px] border border-hairline bg-card px-3.5 pb-3.5 pt-3">
            <div className="flex items-center gap-2.5">
              <StepButton label={`One ${length.unit} less`} disabled={length.value <= 1} onStep={() => length.onChange(length.value - 1)}>
                <Minus className="h-4.5 w-4.5" strokeWidth={2.2} />
              </StepButton>
              <ValueButton
                value={lengthCount}
                line={length.unit === 'day' ? `Until ${dayOf(last)}` : `Leaves ${dayOf(last)}`}
                onClick={() => setSheet('until')}
              />
              <StepButton label={`One ${length.unit} more`} disabled={length.value >= length.max} onStep={() => length.onChange(length.value + 1)}>
                <Plus className="h-4.5 w-4.5" strokeWidth={2.2} />
              </StepButton>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex justify-between text-xs text-ink-muted" aria-hidden>
                <span>{count(1, length.unit)}</span>
                <span>{count(length.max, length.unit)}</span>
              </div>
              <input
                type="range"
                aria-label={`How many ${length.unit}s the PIN works`}
                min={1}
                max={length.max}
                value={length.value}
                onChange={(event) => length.onChange(Number(event.target.value))}
                className="h-8 w-full cursor-pointer"
                style={{ accentColor: 'var(--primary-800)' }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* 4. The days the PIN works, at a glance. */}
      {length || days ? (
        <div className="flex min-h-12 items-center gap-2.5 rounded-[12px] border border-hairline bg-card px-3.5 text-primary-700">
          <CalendarDays className="h-4.5 w-4.5 shrink-0" strokeWidth={2} aria-hidden />
          <span className="min-w-0 flex-1 text-sm font-bold text-ink">{spanOf(start, last)}</span>
          <span className="shrink-0 text-[13px] font-semibold">{lengthCount}</span>
        </div>
      ) : null}

      <DatesSheet
        key={sheet ?? 'closed'}
        mode={sheet}
        onClose={() => setSheet(null)}
        title={sheet === 'until' ? (length?.unit === 'night' ? 'Leaves' : 'Until') : dayLabel}
        start={start}
        length={length}
        days={days ?? 1}
        onSave={(day) => {
          if (sheet === 'day') onStart(day);
          else if (length) length.onChange(differenceInCalendarDays(day, start) + (length.unit === 'day' ? 1 : 0));
          setSheet(null);
        }}
      />
    </div>
  );
}

/** A round − / + or ‹ / › button. Held, it keeps stepping, faster after a moment; a key press steps once. */
function StepButton({ label, disabled, onStep, children }: { label: string; disabled: boolean; onStep: () => void; children: ReactNode }): JSX.Element {
  const timer = useRef<number>();
  // The held repeat reads the latest step, which knows the value after the last one; at a limit it does nothing,
  // since a tick can land before the effect below stops the repeat.
  const latest = useRef(onStep);
  latest.current = disabled ? () => {} : onStep;
  const stop = () => {
    window.clearTimeout(timer.current);
    window.clearInterval(timer.current);
  };
  useEffect(() => {
    if (disabled) stop();
  }, [disabled]);
  useEffect(() => stop, []);

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onPointerDown={() => {
        // One tick per press; the held repeats stay quiet.
        haptics.tap();
        stop();
        latest.current();
        timer.current = window.setTimeout(() => (timer.current = window.setInterval(() => latest.current(), 70)), 400);
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      // A pointer press already stepped on pointer down; only the keyboard's click (detail 0) steps here.
      onClick={(event) => event.detail === 0 && onStep()}
      className="flex h-11 w-11 shrink-0 touch-manipulation select-none items-center justify-center rounded-full border border-hairline bg-card text-ink disabled:opacity-35"
    >
      {children}
    </button>
  );
}

/** The value between the steppers, drawn as a field so it reads as tappable: it opens the calendar. */
function ValueButton({ value, line, onClick }: { value: string; line: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-16 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[12px] border border-hairline bg-card px-2.5 py-2 text-center text-ink hover:border-primary-500"
    >
      <span className="text-lg font-bold leading-tight">{value}</span>
      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-primary-700">
        <CalendarDays className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
        {line}
        <ChevronDown className="h-3 w-3" strokeWidth={2.5} aria-hidden />
      </span>
    </button>
  );
}

interface DatesSheetProps {
  /** Picking the visit day, or the last day; null is closed. */
  mode: 'day' | 'until' | null;
  onClose: () => void;
  title: string;
  start: Date;
  length?: VisitLength;
  days: number;
  onSave: (day: Date) => void;
}

/**
 * The calendar: months one under the other. Picking the visit day shades the days its PIN then works;
 * picking the last day blocks days before the visit and strikes out days past the longest allowed.
 */
function DatesSheet({ mode, onClose, title, start, length, days, onSave }: DatesSheetProps): JSX.Element {
  // 1. Starts on the day already chosen.
  const until = mode === 'until' && length !== undefined;
  const [picked, setPicked] = useState(() => (until ? lastDayOf(start, length, days) : start));

  // 2. The visit and its last day as they would be saved.
  const today = startOfToday();
  const first = until ? start : picked;
  const last = until ? picked : lastDayOf(picked, length, days);

  // 3. Which days can be tapped: the visit day within the booking window; a last day from the shortest to the longest.
  const earliestLast = until ? lastDayOf(start, { ...length, value: 1 }, days) : today;
  const latestLast = until ? lastDayOf(start, { ...length, value: length.max }, days) : addMonths(today, BOOKING_WINDOW_MONTHS);
  const outOfRange = (day: Date) => until && day > latestLast;
  const blocked = (day: Date) => day < today || (until ? day < earliestLast || day > latestLast : day > latestLast);

  // 4. From this month to the last month a day can be picked in.
  const months = Array.from({ length: differenceInCalendarMonths(latestLast, today) + 1 }, (_, index) => addMonths(startOfMonth(today), index));
  const description = until ? `${spanOf(first, last)} · up to ${count(length.max, length.unit)}` : spanOf(first, last);

  return (
    <BottomSheet
      open={mode !== null}
      onOpenChange={(next) => !next && onClose()}
      title={title}
      description={description}
      height="92dvh"
      titleAccessory={
        <button
          type="button"
          aria-label="Close"
          // Kept from the header's drag, which would swallow the click.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-sunken text-ink"
        >
          <ChevronDown className="h-5 w-5" strokeWidth={2} />
        </button>
      }
    >
      <div className="flex min-h-full flex-col">
        {/* 5. The weekdays stay on top. */}
        <div className="sticky top-0 z-10 -mx-6 -mt-4 bg-elevated px-6 hairline-b">
          <div className="grid grid-cols-7 pb-1.5 pt-2.5 text-center text-[11px] font-bold text-ink-muted" aria-hidden>
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
              <span key={index}>{day}</span>
            ))}
          </div>
        </div>

        {/* 6. The months. */}
        <div className="flex-1 pb-3">
          {months.map((month) => (
            <Month
              key={month.getTime()}
              month={month}
              first={first}
              last={last}
              bothEnds={until}
              today={today}
              blocked={blocked}
              struck={outOfRange}
              onPick={setPicked}
            />
          ))}
        </div>

        {/* 7. Save keeps the day picked. */}
        <div className="sticky bottom-0 -mx-6 bg-elevated px-6 pb-4 pt-3 hairline-t">
          <Button width="full" onClick={() => onSave(picked)}>
            Save
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

/**
 * One month, Monday first. The visit day is a filled circle (and the last day too, when picking it);
 * the days between are shaded, the band rounded wherever a week row or the month breaks it.
 */
function Month({
  month,
  first,
  last,
  bothEnds,
  today,
  blocked,
  struck,
  onPick,
}: {
  month: Date;
  first: Date;
  last: Date;
  bothEnds: boolean;
  today: Date;
  blocked: (day: Date) => boolean;
  struck: (day: Date) => boolean;
  onPick: (day: Date) => void;
}): JSX.Element {
  const lead = (month.getDay() + 6) % 7;
  const monthDays = Array.from({ length: endOfMonth(month).getDate() }, (_, index) => addDays(month, index));

  return (
    <section className="pt-3.5">
      <h3 className="pb-1.5 text-[15px] font-bold text-ink">{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</h3>
      <div className="grid grid-cols-7 gap-y-0.5">
        {Array.from({ length: lead }, (_, index) => (
          <span key={`lead-${index}`} />
        ))}
        {monthDays.map((day, index) => {
          const isFirst = isSameDay(day, first);
          const isLast = isSameDay(day, last);
          const filled = isFirst || (bothEnds && isLast);
          const shaded = last > first && day >= first && day <= last;
          const column = (lead + index) % 7;
          return (
            <div
              key={day.getTime()}
              className={cn(
                'flex h-11 items-center justify-center',
                // The band runs from the visit day's centre to the last day's.
                shaded && !isFirst && !isLast && 'bg-primary-50',
                shaded && isFirst && 'bg-[linear-gradient(to_right,transparent_50%,var(--tint-50,var(--primary-50))_50%)]',
                shaded && isLast && 'bg-[linear-gradient(to_left,transparent_50%,var(--tint-50,var(--primary-50))_50%)]',
                shaded && !isFirst && (column === 0 || index === 0) && 'rounded-l-full',
                shaded && !isLast && (column === 6 || index === monthDays.length - 1) && 'rounded-r-full',
              )}
            >
              <button
                type="button"
                disabled={blocked(day)}
                aria-pressed={filled}
                aria-label={dayOf(day)}
                onClick={() => {
                  haptics.tap();
                  onPick(day);
                }}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full text-[15px] font-medium text-ink disabled:text-ink-muted disabled:opacity-60',
                  filled && 'bg-primary-600 font-bold text-primary-ink',
                  !filled && isSameDay(day, today) && 'border border-hairline',
                  struck(day) && 'line-through',
                )}
              >
                {day.getDate()}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
